// ======================== CreateReturn.jsx ========================
import { useEffect, useState, useRef } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import { Formik, FieldArray } from "formik";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";

/* ---------- axios ---------- */
const token = localStorage.getItem("token");

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    Authorization: `Bearer ${token}`,
  },  

});

/* ---------- API helpers ---------- */
const getUsers = () =>
  api.get("/api/user", { params: { _t: Date.now() } }).then((r) =>
    (r.data?.data || []).filter(
      (u) => String(u?.role?.name || "").toLowerCase() === "sales"
    )
  );
const getItems = () =>
  api.get("/api/item", { params: { _t: Date.now() } }).then((r) => r.data?.data || []);
const getWarehouses = () =>
  api.get("/api/warehouse", { params: { _t: Date.now() } }).then((r) => r.data?.data || [] );
const postReturn = (body) =>
  api
    .post("/api/return", body)
    .then((r) => ({ data: r.data }))
    .catch((e) => ({ error: e.response?.data?.message || e.message }));

export default function CreateReturn() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  const storedUser = JSON.parse(localStorage.getItem("user")) || {};
  const userId = storedUser?.id || null;
  const roleName = storedUser?.roleName || "";
  const isSales = roleName.toLowerCase().includes("sales");
  const isNonMobile = useMediaQuery("(min-width:600px)");

  /* ---------- Load dropdowns ---------- */
  const loadOptions = async () => {
    setLoading(true);
    try {
      const [u, i, w] = await Promise.all([
        getUsers(),
        getItems(),
        getWarehouses(),
      ]);
      if (!mounted.current) return;
      setUsers(Array.isArray(u) ? u : []);
      setItems(Array.isArray(i) ? i : []);
      setWarehouses(Array.isArray(w) ? w : []);
    } catch {
      setToast({ open: true, msg: "Failed to load options", sev: "error" });
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    loadOptions();
    const onFocus = () => loadOptions();
    window.addEventListener("focus", onFocus);
    return () => {
      mounted.current = false;
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  /* ---------- Formik ---------- */
  const INITIALS = {
    userId: isSales ? userId : "",
    warehouseId: "",
    type: "",
    returnDate: new Date().toISOString().slice(0, 10),
    reason: "",
    returnTo: "store",
    items: [{ itemId: "", quantity: 1 }],
    description: "",
  };

  const SCHEMA = yup.object({
    userId: isSales ? yup.number() : yup.number().required("Select user"),
    warehouseId: yup.number().required("Select warehouse"),
    type: yup.string().required("Select return type"),
    returnDate: yup.date().required("Return date required"),
    returnTo: yup.string().required("Select return destination"),
    reason: yup.string().required("reason  required"),

    items: yup
      .array()
      .of(
        yup.object({
          itemId: yup.number().required("Select item"),
          quantity: yup
            .number()
            .min(1, "Quantity must be at least 1")
            .required("Quantity required"),
        })
      )
      .min(1, "Add at least one item"),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const payload = {
      userId: isSales ? Number(userId) : Number(values.userId),
      warehouseId: Number(values.warehouseId),
      returnDate: values.returnDate,
      reason: values.reason || undefined,
      description: values.description || undefined,
      type: values.type,
      returnTo: values.returnTo,
      items: values.items.map((i) => ({
        itemId: Number(i.itemId),
        quantity: Number(i.quantity),
      })),
    };

    const { data, error } = await postReturn(payload);
    setSubmitting(false);

    if (error)
      return setToast({ open: true, msg: `Error: ${error}`, sev: "error" });
    setToast({
      open: true,
      msg: "Return created successfully!",
      sev: "success",
    });
    resetForm();
  };

  const LoadingBar = loading ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <CircularProgress size={18} />
      <span style={{ fontSize: 14, opacity: 0.8 }}>Loading dropdowns…</span>
    </Box>
  ) : null;

  return (
    <Box m="20px">
      <Header title="CREATE RETURN" subtitle="Record returned items" />

      {LoadingBar}

      <Formik
        initialValues={INITIALS}
        validationSchema={SCHEMA}
        onSubmit={handleSubmit}
      >
        {(bag) => (
          <form onSubmit={bag.handleSubmit}>
            {/* ---------- First Row: 3 Fields ---------- */}
            <Box
              display="grid"
              gridTemplateColumns={isNonMobile ? "repeat(3, 1fr)" : "1fr"}
              gap={2}
              mb={2}
              sx={{
                "& > div": {
                  gridColumn: isNonMobile ? undefined : "span 1 !important",
                },
              }}
            >
              {!isSales && (
                <TextField
                  select
                  label="User"
                  name="userId"
                  value={bag.values.userId}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={bag.touched.userId && Boolean(bag.errors.userId)}
                  helperText={bag.touched.userId && bag.errors.userId}
                >
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.fullName || u.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                select
                label="Warehouse"
                name="warehouseId"
                value={bag.values.warehouseId}
                onChange={bag.handleChange}
                onBlur={bag.handleBlur}
                variant="filled"
                fullWidth
                error={
                  bag.touched.warehouseId && Boolean(bag.errors.warehouseId)
                }
                helperText={bag.touched.warehouseId && bag.errors.warehouseId}
              >
                {warehouses.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Type"
                name="type"
                value={bag.values.type}
                onChange={bag.handleChange}
                onBlur={bag.handleBlur}
                variant="filled"
                fullWidth
                error={bag.touched.type && Boolean(bag.errors.type)}
                helperText={bag.touched.type && bag.errors.type}
              >
                <MenuItem value="sale">Sales Return</MenuItem>
                <MenuItem value="purchase">Purchase Return</MenuItem>
              </TextField>
            </Box>

            {/* ---------- Second Row: 2 Fields ---------- */}
            <Box
              display="grid"
              gridTemplateColumns={isNonMobile ? "repeat(3, 1fr)" : "1fr"}
              gap={2}
              mb={2}
              sx={{
                "& > div": {
                  gridColumn: isNonMobile ? undefined : "span 1 !important",
                },
              }}
            >
              <TextField
                type="date"
                label="Return Date"
                name="returnDate"
                value={bag.values.returnDate}
                onChange={bag.handleChange}
                InputLabelProps={{ shrink: true }}
                onBlur={bag.handleBlur}
                variant="filled"
                fullWidth
                error={bag.touched.returnDate && Boolean(bag.errors.returnDate)}
                helperText={bag.touched.returnDate && bag.errors.returnDate}
                spans={2}
              />

              <TextField
                label="Reason"
                name="reason"
                value={bag.values.reason}
                onChange={bag.handleChange}
                onBlur={bag.handleBlur}
                variant="filled"
                fullWidth
                error={bag.touched.reason && Boolean(bag.errors.reason)}
                helperText={bag.touched.reason && bag.errors.reason}
              />
              <TextField
                select
                label="Return To"
                name="returnTo"
                value={bag.values.returnTo}
                onChange={bag.handleChange}
                onBlur={bag.handleBlur}
                variant="filled"
                fullWidth
                error={bag.touched.returnTo && Boolean(bag.errors.returnTo)}
                helperText={bag.touched.returnTo && bag.errors.returnTo}
                spans={3}
              >
                <MenuItem value="store">Store</MenuItem>
                <MenuItem value="supplier">Supplier</MenuItem>
              </TextField>
            </Box>

            {/* ---------- Items Array ---------- */}
            <FieldArray name="items">
              {({ push, remove }) => (
                <Box mb={2}>
                  {bag.values.items.map((item, index) => (
                    <Box
                      key={index}
                      display="grid"
                      gap="15px"
                      mt={2}
                      mb={1}
                      p={2}
                      borderRadius={2}
                      border="1px solid #4444"
                      gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                      sx={{
                        "& > div": {
                          gridColumn: isNonMobile
                            ? undefined
                            : "span 4 !important",
                        },
                      }}
                    >
                      <TextField
                        select
                        label="Item"
                        name={`items[${index}].itemId`}
                        value={item.itemId}
                        onChange={bag.handleChange}
                        onBlur={bag.handleBlur}
                        variant="filled"
                        fullWidth
                        error={
                          bag.touched.items?.[index]?.itemId &&
                          Boolean(bag.errors.items?.[index]?.itemId)
                        }
                        helperText={
                          bag.touched.items?.[index]?.itemId &&
                          bag.errors.items?.[index]?.itemId
                        }
                        spans={2}
                      >
                        {items.map((i) => (
                          <MenuItem key={i.id} value={i.id}>
                            {i.name}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        type="number"
                        label="Quantity"
                        name={`items[${index}].quantity`}
                        value={item.quantity}
                        onChange={bag.handleChange}
                        variant="filled"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e")
                            e.preventDefault();
                        }}
                        onInput={(e) => {
                          if (+e.target.value < 0) e.target.value = 0;
                        }}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{ min: 0, step: 0.01 }}
                      />

                      <Button
                        type="button"
                        color="error"
                        onClick={() => remove(index)}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}

                  <Button
                    type="button"
                    onClick={() => push({ itemId: "", quantity: 1 })}
                    variant="outlined"
                  >
                    Add Item
                  </Button>
                </Box>
              )}
            </FieldArray>

            {/* ---------- Description ---------- */}
            <TextField
              label="Description"
              name="description"
              value={bag.values.description}
              onChange={bag.handleChange}
              multiline
              rows={3}
              variant="filled"
              fullWidth
              sx={{ mb: 2 }}
            />

            {/* ---------- Submit Buttons ---------- */}
            <Box display="flex" justifyContent="end" gap={2}>
              <Button type="reset" onClick={bag.handleReset} variant="outlined">
                Reset
              </Button>
              <Box position="relative" display="inline-flex">
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={bag.isSubmitting} // disable while loading
                >
                  Create Return
                </Button>

                {bag.isSubmitting && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: "primary.main",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px", // half of size
                      marginLeft: "-12px",
                    }}
                  />
                )}
              </Box>
            </Box>
          </form>
        )}
      </Formik>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert severity={toast.sev} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
